import React, { useEffect, useState } from "react";
import throughShipmentApi, { InvoiceInfo } from "@api/throughShipment/throughShipmentApi";
import PageTitle from "@component/PageTitle";
import {
  externalOrderState,
  fetchApprovalExternalOrder,
  fetchDeleteExternalOrder,
  fetchExternalOrderInfo,
  fetchExternalOrdersRevenue,
} from "@redux/externalOrderSlice";
import { Button, message, Modal, Table } from "antd";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/store";
import { useNavigate } from "react-router-dom";
import { StaffPermission, usePermission } from "@hooks/usePermission";
import useIntRouteParam from "@hooks/useIntRouteParam";
import urlParam from "@utils/urlParam";
import CreateInvoiceModal from "../CreateInvoiceModal";
import OrderDetail from "./OrderDetail";
import ContractDetail from "../OrderDisplayComponent/ContractDetail";
import OrderDeductionSection from "../OrderDisplayComponent/OrderDeductionSection";
import UploadFileButton from "../UploadFileButton";
import OrderInfoTable from "../OrderDisplayComponent/OrderInfoTable";
import OrderProductTable from "../OrderDisplayComponent/OrderProductTable";
import OrderSummaryTable from "../OrderDisplayComponent/OrderSummaryTable";
import {
  Divide,
  Flex,
  HeaderButton,
  HeaderRow,
  Row,
  Title,
  Wrapper,
  NormalTitle,
} from "../OrderDisplayComponent/style";

const REDIRECT_URL = "/operation-of-brand/external-order";

export default function OrderReview() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { externalOrderInfo, externalOrdersRevenueResult } = useSelector(externalOrderState);
  const externalOrderId = useIntRouteParam("externalOrderId", true);
  const [showInvoiceModal, setShowInvoiceModal] = useState<number | undefined>();
  const financeAccess = usePermission(StaffPermission.CAN_ACCESS_FINANCE_PAGE);
  const externalOrdersRevenueLists = externalOrdersRevenueResult?.results ?? [];

  useEffect(() => {
    const id = urlParam.getValidId(externalOrderId);
    if (id === null) {
      navigate(REDIRECT_URL);
      return;
    }
    dispatch(fetchExternalOrderInfo(id))
      .unwrap()
      .catch(() => {
        navigate(REDIRECT_URL);
        return null;
      });
    dispatch(fetchExternalOrdersRevenue(id));
  }, [dispatch, externalOrderId, navigate]);

  if (!externalOrderId || !externalOrderInfo) {
    return null;
  }

  const handelOnCreateInvoice = async (orderId: number, invoiceInfo: InvoiceInfo) => {
    try {
      await throughShipmentApi.fetchCreateInvoice(orderId, invoiceInfo);
      message.success("開立發票成功");
      setShowInvoiceModal(undefined);
    } catch (e) {
      message.error("開立發票失敗");
    }
  };
  const cantApprove =
    externalOrderInfo?.approvalStatus === "審核完成" ||
    externalOrderInfo?.approvalStatus === "審核未通過" ||
    externalOrderInfo?.shippingStatus === "訂單處理中(已拋單)" ||
    !externalOrderInfo.isSend;
  const cantEdit =
    externalOrderInfo?.shippingStatus === "訂單處理中(已拋單)" || externalOrderInfo?.approvalStatus === "審核未通過";

  return (
    <>
      <PageTitle title="出貨訂單管理" />
      <HeaderRow>
        <Title>訂單檢視</Title>
        <Flex>
          {externalOrderInfo?.deliveryAt && (
            <HeaderButton
              onClick={() => {
                if (financeAccess) {
                  setShowInvoiceModal(externalOrderId);
                } else {
                  message.error("無權限開立發票");
                }
              }}
              disabled={!externalOrderInfo?.canInvoice}
            >
              開立發票
            </HeaderButton>
          )}
          <HeaderButton
            onClick={() => dispatch(fetchApprovalExternalOrder({ externalOrderId, isApproved: false }))}
            disabled={cantApprove}
          >
            審核不通過
          </HeaderButton>
          <HeaderButton
            onClick={() => dispatch(fetchApprovalExternalOrder({ externalOrderId, isApproved: true }))}
            disabled={cantApprove}
          >
            審核通過
          </HeaderButton>
          <HeaderButton
            type="primary"
            disabled={cantEdit}
            onClick={() => window.open(`/operation-of-brand/external-order/${externalOrderId}`)}
          >
            編輯
          </HeaderButton>
          <Button
            disabled={
              externalOrderInfo.shippingStatus === "訂單處理中(已拋單)" ||
              externalOrderInfo.approvalStatus === "審核完成" ||
              externalOrderInfo.approvalStatus === "作廢" ||
              externalOrderInfo.approvalStatus === "審核未通過" ||
              externalOrderInfo.isSend
            }
            onClick={() => {
              Modal.warning({
                title: "提醒：作廢後需作廢倉庫批號",
                okText: "我知道了",
                onOk: () => {
                  dispatch(fetchDeleteExternalOrder(externalOrderId));
                },
              });
            }}
          >
            作廢
          </Button>
        </Flex>
      </HeaderRow>
      <Wrapper>
        {externalOrderInfo.invoiceInfo.invoiceNumber && (
          <>
            <Row marginBottom="20px">
              <Title>發票資訊</Title>
            </Row>
            <Row marginBottom="20px">
              <Table
                columns={[
                  {
                    key: "invoiceNumber",
                    dataIndex: "invoiceNumber",
                    title: "發票號碼",
                    width: 200,
                  },
                  {
                    key: "invoiceDate",
                    dataIndex: "invoiceDate",
                    title: "發票開立日",
                    width: 200,
                  },
                  {
                    key: "invoiceAmount",
                    dataIndex: "invoiceAmount",
                    title: "發票總金額",
                    width: 200,
                  },
                ]}
                dataSource={[externalOrderInfo.invoiceInfo]}
                pagination={false}
                bordered
              />
            </Row>
          </>
        )}
        <OrderDetail externalOrderInfo={externalOrderInfo} />

        <OrderInfoTable externalOrderInfo={externalOrderInfo} title="預估訂單營收/成本/毛利總覽" type="expected" />
        <OrderInfoTable externalOrderInfo={externalOrderInfo} title="最終訂單營收/成本/毛利總覽" type="final" />

        <Divide />
        <ContractDetail externalOrderInfo={externalOrderInfo} />
        <Divide />
        <OrderDeductionSection externalOrderInfo={externalOrderInfo} />
        <Divide />
        <OrderProductTable externalOrderInfo={externalOrderInfo} externalOrderId={externalOrderId} mode="review" />
        <Row marginTop="20px">
          <OrderSummaryTable
            externalOrderInfo={externalOrderInfo}
            externalOrdersRevenueLists={externalOrdersRevenueLists}
            usePackageSet
          />
        </Row>
        <Row marginTop="20px">
          <NormalTitle>上傳麥頭</NormalTitle>
          <UploadFileButton value={externalOrderInfo.matoL ?? []} isSend />
        </Row>
      </Wrapper>
      {showInvoiceModal && (
        <CreateInvoiceModal
          onClose={() => setShowInvoiceModal(undefined)}
          onSubmit={handelOnCreateInvoice}
          orderId={showInvoiceModal}
        />
      )}
    </>
  );
}
